import React, { useMemo } from "react";
import { Train, ArrowDown, ChevronRight, Split, AlertTriangle } from "lucide-react";
import { assessMobilityChain, MOBILITY_ASSUMPTIONS } from "../services/mobilityChain";

/* §11 Mobility Intelligence.
 *
 * Leads with the propagation, not the stage list. A commander does not need
 * to be told the bus terminal is busy — they need to be told that the bus
 * terminal being busy becomes crowd pressure at the ghat in 23 minutes.
 * The stage table is underneath for anyone who wants to check the working.
 */
export default function MobilityPanel({ lang = "en", live, twin, onFocus }) {
  const L = (o) => o?.[lang] ?? o?.en ?? "";
  const chain = useMemo(
    () => assessMobilityChain(live.state, { twin }),
    [live.state, twin],
  );

  const STATE_CLS = {
    held: "bg-slate-200 text-slate-700 border-slate-400",
    overloaded: "bg-red-100 text-red-800 border-red-300",
    saturated: "bg-orange-100 text-orange-800 border-orange-300",
    tight: "bg-amber-100 text-amber-800 border-amber-300",
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };

  const toGhat = chain.propagation.filter((p) => p.toStage === "ghat").slice(0, 2);

  return (
    <div className="border-t border-slate-200 px-3 py-3">
      <div className="mb-2 flex items-center gap-1.5">
        <Train size={12} className="text-slate-500" />
        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
          {lang === "mr" ? "गतिशीलता साखळी" : "Mobility Chain"}
        </span>
        <span className="ml-auto rounded border border-fuchsia-300 bg-fuchsia-100 px-1.5 py-0.5 text-[8px] font-black text-fuchsia-800">
          SIM
        </span>
      </div>

      {/* The binding constraint and who owns it — the answer most worth
          having, because the department that must act is rarely the one
          reporting the symptom. */}
      {chain.binding && (
        <div className="mb-2 rounded border border-slate-300 bg-slate-50 px-2 py-1.5">
          <div className="text-[9px] font-black uppercase tracking-wider text-slate-500">
            {lang === "mr" ? "बंधनकारक मर्यादा" : "Binding constraint"}
          </div>
          <div className="text-[11px] font-bold text-slate-900">
            {L(chain.binding.label)}
            <span className="ml-1 text-[9px] font-medium text-slate-500">
              {chain.binding.agency}
            </span>
          </div>
          <div className="text-[9px] tabular-nums text-slate-600">
            {chain.chainThroughputPerMin.toLocaleString("en-IN")}/min{" "}
            {lang === "mr" ? "एकूण क्षमता" : "chain throughput"} ·{" "}
            {chain.headDemandPerMin.toLocaleString("en-IN")}/min{" "}
            {lang === "mr" ? "मागणी" : "demand"}
          </div>
        </div>
      )}

      {/* Propagation — the §11 payload. */}
      {toGhat.length > 0 && (
        <div className="mb-2 space-y-1">
          {toGhat.map((p) => (
            <div
              key={`${p.fromStage}-${p.toStage}`}
              className={`rounded border px-2 py-1.5 ${
                p.effect === "shortfall"
                  ? "border-sky-300 bg-sky-50"
                  : "border-orange-300 bg-orange-50"
              }`}
            >
              <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-wider">
                <ArrowDown size={9} />
                {p.effect === "shortfall"
                  ? (lang === "mr" ? "कमतरता" : "Shortfall")
                  : (lang === "mr" ? "दबाव" : "Surge")}
                <span className="ml-auto tabular-nums">T+{p.inMinutes}m</span>
              </div>
              <p className="text-[10px] leading-snug text-slate-800">{p.narrative}</p>
            </div>
          ))}
        </div>
      )}

      {/* Stage table. Parallel channels are marked, because reading them as
          a queue is the mistake this model exists to avoid. */}
      <table className="w-full text-[9px]">
        <tbody>
          {chain.stages.map((s) => (
            <tr key={s.stage} className="border-t border-slate-100">
              <td className="py-1 pr-1">
                <button
                  type="button"
                  onClick={() => s.assets?.[0] && onFocus?.({ id: s.assets[0] })}
                  className="flex items-center gap-1 text-left text-slate-700"
                >
                  {s.parallel && <Split size={8} className="flex-shrink-0 text-slate-400" />}
                  <span className="truncate">{L(s.label)}</span>
                </button>
              </td>
              <td className="py-1 text-right tabular-nums text-slate-500">
                {s.demandPerMin.toLocaleString("en-IN")}
              </td>
              <td className="py-1 pl-1 text-right">
                <span className={`rounded border px-1 py-0.5 text-[8px] font-black ${STATE_CLS[s.state]}`}>
                  {s.utilisation == null ? "HELD" : `${Math.round(s.utilisation * 100)}%`}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-1 flex items-start gap-1 text-[9px] leading-snug text-slate-400">
        <Split size={8} className="mt-0.5 flex-shrink-0" />
        {lang === "mr"
          ? "रेल्वे, बस आणि वाहनतळ समांतर प्रवेश मार्ग आहेत — रांग नाही."
          : "Rail, bus and parking are parallel ingress channels, not a queue. A pilgrim arriving by train does not then use a car park."}
      </p>

      {chain.stages.some((s) => s.state === "overloaded") && (
        <p className="mt-1 flex items-start gap-1 text-[9px] leading-snug text-amber-700">
          <AlertTriangle size={9} className="mt-0.5 flex-shrink-0" />
          {lang === "mr"
            ? "संतृप्त प्रवेश मार्ग फक्त तो वापरणाऱ्यांना मर्यादित करतो."
            : "A saturated ingress channel constrains only the people using it, which is why it is not blamed for the whole theatre."}
        </p>
      )}

      <details className="mt-2">
        <summary className="flex cursor-pointer items-center gap-0.5 text-[9px] font-bold text-slate-400 hover:text-slate-600">
          <ChevronRight size={8} />
          {lang === "mr" ? "गृहीतके" : "Method"}
        </summary>
        <ul className="mt-1 space-y-0.5">
          {MOBILITY_ASSUMPTIONS.map((a) => (
            <li key={a} className="text-[9px] leading-snug text-slate-500">· {a}</li>
          ))}
        </ul>
      </details>
    </div>
  );
}
